import { UISref, useRouter } from "@uirouter/react";
import moment from "moment";
import { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../../../../components/button";
import { Icon } from "../../../../components/icons";
import { KycIcon } from "../../../../components/icons/status";
import { Loader } from "../../../../components/loader";
import { Table } from "../../../../components/table";
import { TableHeaders } from "../../../../components/table/Head";
import { Widget } from "../../../../components/widget";
import { Pageable } from "../../../../services/pageable";
import { AppStore } from "../../../../store";
import { useFetchable } from "../../../../store/common/fetchable";
import { countries } from "../../../../store/common/models";
import { validationFields } from "../../../../store/common/models/fields";
import { VerificationActions } from "../../../../store/verification/actions";
import { VerificationDTO } from "../../../../store/verification/models/dto/kyc";
import { Verification } from "../../../../store/verification/reducer";
import { colours } from "../../../../styles/colours";
import { FraudRoutes } from "../../routes";
import { WidgetAdornment } from "./adornment";
import { NoKyb } from "./empty";

type SingleKyb = VerificationDTO.Responses.GetSingleKyb.T;
type fieldNames = keyof SingleKyb;

function useHeaders(): TableHeaders<fieldNames> {
  const companyRegistrationNumber = validationFields.companyRegistrationNumber.useName();
  const country = validationFields.country.useName();
  const createdDate = validationFields.createdDate.useName();

  return useMemo(
    () => [
      {
        name: "companyRegistrationNumber",
        title: companyRegistrationNumber,
      },
      {
        name: "verificationData",
        title: "Company Name",
        hiddenMobile: true,
      },
      {
        name: "companyJurisdictionCountryCode",
        title: country,
        hiddenMobile: true,
      },
      {
        name: "createdDate",
        title: createdDate,
        hiddenMobile: true,
      },
      {
        name: "status",
        title: "Status",
      },
      {
        name: "fraudCheckId",
        title: "",
        centered: true,
        hiddenMobile: true,
      },
    ],
    [companyRegistrationNumber, country, createdDate]
  );
}

export const AllKyb = () => {
  const data = useFetchable(
    Verification.allKyb,
    useMemo<Pageable.T<keyof VerificationDTO.Responses.GetSingleKyb.T>>(
      () => ({
        pagination: {
          size: 20,
          page: 0,
        },
        sorting: {
          direction: "desc",
          field: "createdDate",
        },
      }),
      []
    )
  );

  return (
    <Loader data={[data]}>
      {([fulfilled]) => <PresentationalAllKyb kybList={fulfilled} />}
    </Loader>
  );
};

const PresentationalAllKyb: FunctionComponent<{
  kybList: VerificationDTO.Responses.GetAllKyb.T;
}> = ({ kybList }) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const headers = useHeaders();

  const pageableState = useSelector<
    AppStore,
    AppStore["verification"]["allKyb"]["pagination"]
  >((store) => store.verification.allKyb.pagination);

  const onPaginationChange = (x: Pageable.T<fieldNames>) =>
    dispatch(VerificationActions.setAllKybPagination(x));

  return (
    <Widget title="All KYBs" adornment={<WidgetAdornment />}>
      {kybList.pageable.totalElements < 1 ? (
        <NoKyb />
      ) : (
        <Table<SingleKyb, fieldNames, never>
          data={kybList.content}
          head={headers}
          renderFields={{
            fraudCheckId: ({ fraudCheckId }) => (
              <UISref
                to={FraudRoutes.names.singleBusinessKyb}
                params={{ fraudCheckId }}
              >
                <a>
                  <PrimaryButton variant="contained">
                    <Icon
                      type="export"
                      rotateAmount={270}
                      colour1={colours.white}
                    />
                  </PrimaryButton>
                </a>
              </UISref>
            ),
            companyJurisdictionCountryCode: ({
              companyJurisdictionCountryCode,
            }) => (
              <>
                {countries.enum[
                  companyJurisdictionCountryCode?.toUpperCase() as typeof countries.T
                ] || companyJurisdictionCountryCode}
              </>
            ),
            createdDate: ({ createdDate }) => (
              <>{moment.utc(createdDate).local().format("yyyy-MM-DD HH:mm")}</>
            ),
            verificationData: ({ verificationData }) => (
              <>{verificationData?.kyb?.companyName}</>
            ),
            status: ({ status }) => KycIcon(status, "kyb"),
          }}
          pagination={kybList.pageable}
          onPaginationChange={onPaginationChange}
          pageableState={pageableState}
          rowOnClick={({ fraudCheckId }) => {
            router.stateService.go(FraudRoutes.names.singleBusinessKyb, {
              fraudCheckId,
            });
          }}
        />
      )}
    </Widget>
  );
};
